/**
 * Слот рабочего места: код / граф+таблица / дерево / сетка / код+холст (черепаха/робот/игра).
 */
import { forwardRef } from 'react';
import UnifiedEditor from './UnifiedEditor';
import { WORKSPACE_KIND } from '../utils/workAttemptHelpers';
import CollaborativeYdocHost from './workspace/CollaborativeYdocHost';
import GraphTableWorkspace from './workspace/GraphTableWorkspace';
import SpreadsheetWorkspace from './workspace/SpreadsheetWorkspace';
import CodeCanvas from './workspace/CodeCanvas';

const CANVAS_KINDS = new Set([
  WORKSPACE_KIND.CODE_TURTLE,
  WORKSPACE_KIND.CODE_ROBOT,
  WORKSPACE_KIND.CODE_CANVAS,
]);

const SIZE_DEFAULTS = {
  [WORKSPACE_KIND.CODE_CANVAS]: { width: 480, height: 480 },
  [WORKSPACE_KIND.CODE_TURTLE]: { width: 280, height: 360 },
  [WORKSPACE_KIND.CODE_ROBOT]: { width: 280, height: 360 },
};

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

/** Ширина/высота холста: дефолт по kind + workspace_config.width/height. */
export function resolveCanvasSize(kind, config = {}) {
  const defaults = SIZE_DEFAULTS[kind] || { width: 280, height: 360 };
  const cfg = config && typeof config === 'object' ? config : {};
  const widthRaw = Number(cfg.width);
  const heightRaw = Number(cfg.height);
  return {
    width: Number.isFinite(widthRaw) && widthRaw > 0
      ? clamp(Math.round(widthRaw), 200, 800)
      : defaults.width,
    height: Number.isFinite(heightRaw) && heightRaw > 0
      ? clamp(Math.round(heightRaw), 200, 700)
      : defaults.height,
  };
}

function canvasModeForKind(kind) {
  if (kind === WORKSPACE_KIND.CODE_ROBOT) return 'robot';
  if (kind === WORKSPACE_KIND.CODE_CANVAS) return 'game';
  return 'turtle';
}

function canvasTitleForKind(kind) {
  if (kind === WORKSPACE_KIND.CODE_ROBOT) return 'Робот';
  if (kind === WORKSPACE_KIND.CODE_CANVAS) return 'Холст (игра)';
  return 'Холст черепахи';
}

const UnifiedWorkspace = forwardRef(function UnifiedWorkspace({
  workspace,
  // collaborative session
  sessionId,
  taskId = null,
  taskSlug = null,
  onYdocReady,
  onStatusChange,
  onContentChange,
  onWorkspaceStateChange,
  seedWorkspaceState,
  // code editor
  theme,
  onThemeChange,
  onRunRequest,
  editorSettings,
  styleLintEnabled,
  onStyleLintChange,
  onFormattingChange,
  reconnectNonce = 0,
}, ref) {
  const kind = workspace?.kind || WORKSPACE_KIND.CODE;
  const config = workspace?.config || {};
  const dataKey = `t${taskId ?? 'x'}:${kind}`;

  if (!workspace?.showWorkspace) return null;

  if (kind === WORKSPACE_KIND.GRAPH_TABLE || kind === WORKSPACE_KIND.TREE_TABLE) {
    return (
      <CollaborativeYdocHost
        ref={ref}
        sessionId={sessionId}
        onYdocReady={onYdocReady}
        onStatusChange={onStatusChange}
        reconnectNonce={reconnectNonce}
      >
        {({ ydoc }) => (
          <GraphTableWorkspace
            kind={kind}
            ydoc={ydoc}
            workspaceConfig={config}
            seedState={seedWorkspaceState}
            dataKey={dataKey}
            onStateChange={onWorkspaceStateChange}
          />
        )}
      </CollaborativeYdocHost>
    );
  }

  if (kind === WORKSPACE_KIND.SPREADSHEET) {
    return (
      <CollaborativeYdocHost
        ref={ref}
        sessionId={sessionId}
        onYdocReady={onYdocReady}
        onStatusChange={onStatusChange}
        reconnectNonce={reconnectNonce}
      >
        {({ ydoc }) => (
          <SpreadsheetWorkspace
            ydoc={ydoc}
            workspaceConfig={config}
            seedState={seedWorkspaceState}
            dataKey={dataKey}
            onStateChange={onWorkspaceStateChange}
            taskSlug={taskSlug}
          />
        )}
      </CollaborativeYdocHost>
    );
  }

  const withCanvas = CANVAS_KINDS.has(kind);
  const canvasConfig = {
    mode: canvasModeForKind(kind),
    ...config,
  };
  const { width: canvasWidth, height: canvasHeight } = resolveCanvasSize(kind, config);

  // code / code_turtle / code_robot / code_canvas
  return (
    <div className={`unified-workspace-code ${withCanvas ? 'with-turtle with-canvas' : ''}`}>
      <div className="unified-workspace-code-main">
        <UnifiedEditor
          ref={ref}
          mode="collaborative"
          sessionId={sessionId}
          onContentChange={onContentChange}
          theme={theme}
          onThemeChange={onThemeChange}
          onYdocReady={onYdocReady}
          onRunRequest={onRunRequest}
          onStatusChange={onStatusChange}
          editorSettings={editorSettings}
          styleLintEnabled={styleLintEnabled}
          onStyleLintChange={onStyleLintChange}
          onFormattingChange={onFormattingChange}
          reconnectNonce={reconnectNonce}
        />
      </div>
      {withCanvas ? (
        <div
          className="unified-workspace-turtle unified-workspace-canvas"
          style={{
            flex: `0 0 ${canvasWidth}px`,
            maxWidth: '50%',
            minWidth: 0,
          }}
        >
          <CodeCanvas
            mode={canvasModeForKind(kind)}
            config={canvasConfig}
            title={canvasTitleForKind(kind)}
            height={canvasHeight}
          />
        </div>
      ) : null}
    </div>
  );
});

export default UnifiedWorkspace;
